import { TESLA_FACTS, type TeslaModelKey } from './tesla-facts'
import { fmtFileSize } from '@/lib/tls-format'
import type { Sound } from './supabase-types'

export type TeslaModelSlug = TeslaModelKey

/** Every model, in the order the facts file lists them. */
const MODEL_SLUGS = Object.keys(TESLA_FACTS.support.models) as TeslaModelSlug[]

/**
 * Name and first model year per line, read from TESLA_FACTS. Nothing here is a
 * literal: add or correct a model in lib/tesla-facts.ts and this follows.
 */
export const TESLA_MODELS = Object.fromEntries(
  MODEL_SLUGS.map((slug) => [
    slug,
    {
      name: TESLA_FACTS.support.models[slug].label,
      firstYear: TESLA_FACTS.support.firstModelYear[slug],
    },
  ]),
) as Record<TeslaModelSlug, { name: string; firstYear: number }>

const EARLIEST_MODEL_YEAR = Math.min(...MODEL_SLUGS.map((slug) => TESLA_MODELS[slug].firstYear))

// Year buckets used by the programmatic /sounds/for/[model]/[year] landing pages.
// Aligned with the platform shifts referenced in PRODUCT.md + the install guides
// (pre-PWS, mid-life, refresh, latest). `openStart` marks the bucket that means
// "every year before max", which exists for any model that shipped before it ends.
export const YEAR_BUCKETS: ReadonlyArray<{
  slug: string
  min: number
  max: number
  label: string
  openStart?: true
}> = [
  { slug: 'pre-2019', min: EARLIEST_MODEL_YEAR, max: 2018, label: 'Pre-2019', openStart: true },
  { slug: '2019-2021', min: 2019, max: 2021, label: '2019–2021' },
  { slug: '2022-2024', min: 2022, max: 2024, label: '2022–2024' },
  { slug: '2025-plus', min: 2025, max: 2030, label: '2025+' },
]

export type YearBucket = (typeof YEAR_BUCKETS)[number]

/**
 * The year buckets that are true for a given model. A range bucket is cut to
 * the model's first year so its slug and label never name a year the model did
 * not exist in: Model Y gets 2020–2021 (not 2019–2021), Cybertruck gets 2024
 * (not 2022–2024). The open-ended Pre-2019 bucket exists for any model that
 * shipped before 2019. There is no Pre-2019 Cybertruck.
 *
 * TES-1546: this is the single source of truth for which model×year pages
 * exist. `generateStaticParams` and the on-page cross-links both read it, so
 * the route can never link to a combination it refuses to serve.
 */
export function yearBucketsForModel(slug: TeslaModelSlug): YearBucket[] {
  const { firstYear } = TESLA_MODELS[slug]
  return YEAR_BUCKETS.filter((bucket) => firstYear <= bucket.max).map((bucket) =>
    bucket.openStart || firstYear <= bucket.min ? bucket : clipBucket(bucket, firstYear),
  )
}

/** A range bucket cut to the model's first year, so its slug and label name only years it existed in. */
function clipBucket(bucket: YearBucket, firstYear: number): YearBucket {
  const single = firstYear === bucket.max
  return {
    slug: single ? `${firstYear}` : `${firstYear}-${bucket.max}`,
    min: firstYear,
    max: bucket.max,
    label: single ? `${firstYear}` : `${firstYear}–${bucket.max}`,
  }
}

export type BucketSupport = 'yes' | 'partial' | 'no'

/**
 * Whether custom lock sounds work for a model in a year bucket, from
 * TESLA_FACTS.support.speakerSince. `partial` means the bucket straddles the
 * cutoff (Model 3 2019: September builds only; Model S/X 2019–2021: only the
 * 2021 refresh), so the honest answer depends on the car.
 */
export function bucketSupport(slug: TeslaModelSlug, bucket: Pick<YearBucket, 'min' | 'max'>): BucketSupport {
  const since = TESLA_FACTS.support.speakerSince[slug]
  if (!since) return 'yes'
  if (bucket.max < since.year) return 'no'
  if (bucket.min > since.year) return 'yes'
  if (bucket.min === since.year && !since.partialYear) return 'yes'
  return 'partial'
}

const MAX_LOCKCHIME_BYTES = 1024 * 1024 // 1 MB recommended lock-chime ceiling

export type InstallWarningReason = 'wrong_format' | 'file_size_over_1mb'

export interface SoundInstallWarning {
  reason: InstallWarningReason
  /** The problem, stated flat. Rendered at --tls-fg-strong, weight 700. */
  verdict: string
  /** What to do about it. Rendered at --tls-fg-muted, weight 600. */
  explanation: string
}

/**
 * Install problems that are properties of the SOUND, not of the visitor's car.
 *
 * Deliberately vehicle-free (2026-08-15). Every active sound in the catalog
 * carries all five models in `compatible_models`, so a per-sound "verified for
 * your model" verdict was a constant dressed up as personalization. The two
 * checks that DO vary per sound (format and file size) vary for everyone, so
 * they are surfaced to everyone rather than only to visitors who had filled in
 * a vehicle form.
 *
 * The one genuinely vehicle-specific fact (a Model 3 built before September
 * 2019 has no external pedestrian-warning speaker and so cannot play any
 * custom lock sound) is a site-wide fact, not a per-sound badge. It lives in
 * TESLA_FACTS.support.models and is stated in /guide and /faq.
 *
 * Returns every applicable warning, most disqualifying first. An empty array
 * means the file installs as shipped.
 */
export function soundInstallWarnings(
  sound: Pick<Sound, 'format' | 'size'>,
): SoundInstallWarning[] {
  const warnings: SoundInstallWarning[] = []

  if (sound.format && sound.format.toLowerCase() !== 'wav') {
    warnings.push({
      reason: 'wrong_format',
      verdict: `This file is ${sound.format.toUpperCase()}, not WAV.`,
      explanation: `Tesla Boombox only reads WAV. Convert it to WAV and name it ${TESLA_FACTS.audio.requiredFilename} before copying it to the USB drive.`,
    })
  }

  const bytes = parseSizeStringToBytes(sound.size)
  if (bytes !== null && bytes > MAX_LOCKCHIME_BYTES) {
    warnings.push({
      reason: 'file_size_over_1mb',
      verdict: `This file is ${fmtFileSize(bytes)}. Keep a lock chime ${TESLA_FACTS.audio.recommended.maxFileSize}.`,
      explanation:
        'Larger files can fail to load from the USB drive. Trim it or re-encode it before copying it across.',
    })
  }

  return warnings
}

export function parseSizeStringToBytes(size?: string): number | null {
  if (!size) return null
  const m = size.trim().match(/^([\d.]+)\s*(B|KB|MB|GB)?$/i)
  if (!m) return null
  const n = Number(m[1])
  if (Number.isNaN(n)) return null
  const unit = (m[2] || 'B').toUpperCase()
  const mult: Record<string, number> = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3 }
  return n * (mult[unit] ?? 1)
}

export function yearBucketForYear(year: number): typeof YEAR_BUCKETS[number] {
  return (
    YEAR_BUCKETS.find((b) => year >= b.min && year <= b.max) ??
    YEAR_BUCKETS[YEAR_BUCKETS.length - 1]
  )
}

export function modelSlugFromCompatibleModelString(s: string): TeslaModelSlug | null {
  const norm = s.toLowerCase().replace(/\s+/g, '-').replace('tesla-', '')
  if (norm in TESLA_MODELS) return norm as TeslaModelSlug
  if (norm === 'modely' || norm === 'y') return 'model-y'
  if (norm === 'model3' || norm === '3') return 'model-3'
  if (norm === 'models' || norm === 's') return 'model-s'
  if (norm === 'modelx' || norm === 'x') return 'model-x'
  if (norm === 'ct' || norm === 'cyber-truck') return 'cybertruck'
  return null
}
